import {Button, createStyles, Group, Modal, Stack, Text, UnstyledButton} from '@mantine/core';
import React, {useState} from 'react';

import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import copyToClipboard from 'util/clipboard/copyToClipboard';

// The key field is a product-specific widget the design system doesn't model: a bordered box holding
// the monospace secret and a Copy segment. The matching 44px height + line-height on both children is
// what vertically centers their text.
const FIELD_HEIGHT = 44;

const useStyles = createStyles((theme) => ({
  field: {
    display: 'flex',
    border: `1px solid ${theme.colors.gray[4]}`,
    borderRadius: theme.radius.smplus,
    overflow: 'hidden',
    // The value input suppresses its own outline (below), so surface keyboard focus on the wrapper.
    '&:focus-within': {
      borderColor: theme.colors.blue[7],
      boxShadow: `0 0 0 1px ${theme.colors.blue[7]}`,
    },
  },
  value: {
    flex: 1,
    minWidth: 0,
    height: FIELD_HEIGHT,
    padding: '0 14px',
    border: 'none',
    outline: 'none',
    backgroundColor: theme.white,
    color: theme.colors.gray[9],
    fontFamily: theme.fontFamilyMonospace,
    fontSize: theme.fontSizes.lg,
    lineHeight: `${FIELD_HEIGHT}px`,
    textOverflow: 'ellipsis',
  },
  copy: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.xs,
    height: FIELD_HEIGHT,
    padding: '0 18px',
    backgroundColor: theme.colors.gray[0],
    color: theme.colors.blue[7],
    fontSize: theme.fontSizes.lg,
    fontWeight: theme.other.fontWeights.semibold,
    lineHeight: `${FIELD_HEIGHT}px`,
    whiteSpace: 'nowrap',
    '&:hover': {backgroundColor: theme.colors.gray[1]},
  },
  copied: {
    backgroundColor: theme.colors.green[0],
    color: theme.colors.green[8],
    '&:hover': {backgroundColor: theme.colors.green[0]},
  },
}));

export function SuccessfulAPIKeyCreationModal({
  apiKey,
  expiresAt,
  onRequestClose,
}: {
  apiKey: string;
  expiresAt?: string | null;
  onRequestClose: () => void;
}): JSX.Element {
  const {classes, cx} = useStyles();
  const [copied, setCopied] = useState(false);
  const warningText =
    "Copy and store your API key in a secure location. Keys are only shown once, and you won't be able to view it after closing the window.";

  const handleCopy = (): void => {
    // No page-level toast: the button confirms the copy in place instead.
    copyToClipboard(apiKey, {
      showToastOnSuccess: false,
      onSuccess: () => {
        setCopied(true);
      },
    });
  };

  const showExpiry = expiresAt != null;

  return (
    <Modal
      title="Your API key is ready"
      opened={true}
      onClose={onRequestClose}
      closeButtonProps={{'aria-label': 'Close'}}
    >
      <Stack spacing="md">
        <div className={classes.field}>
          <input readOnly={true} value={apiKey} aria-label="API key" className={classes.value} />
          <UnstyledButton className={cx(classes.copy, copied && classes.copied)} onClick={handleCopy}>
            <BenchlingIcon icon={copied ? 'check' : 'copy'} />
            {copied ? 'Copied' : 'Copy'}
          </UnstyledButton>
        </div>
        {showExpiry && (
          <Text size="sm" c="dimmed">
            {`Expires ${expiresAt}`}
          </Text>
        )}
        <InlineBanner bannerType={InlineBannerType.WARNING} text={warningText} />
        <Group position="right" mt="xs">
          <Button variant="primary" onClick={onRequestClose} data-autofocus={true}>
            Done
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

export default SuccessfulAPIKeyCreationModal;
